import { useCallback, useMemo } from "react";

import { getObjectEntries, objectFromEntries } from "metabase/lib/objects";
import { isNotNull } from "metabase/lib/types";
import type {
  DimensionMetadata,
  MetricDefinition,
  ProjectionClause,
} from "metabase-lib/metric";
import * as LibMetric from "metabase-lib/metric";
import type {
  CardId,
  Dataset,
  MetricBreakoutValuesResponse,
  SingleSeries,
} from "metabase-types/api";

import type { MetricsViewerPageProps } from "../pages/MetricsViewerPage/MetricsViewerPage";
import type {
  MetricSourceId,
  MetricsViewerDefinitionEntry,
  MetricsViewerTabState,
  SelectedMetric,
  SourceBreakoutColorMap,
  SourceColorMap,
} from "../types/viewer-state";
import {
  applyProjection,
  buildBinnedBreakoutDefinition,
  getDefinitionName,
} from "../utils/definition-builder";
import { buildDimensionFilterClause } from "../utils/dimension-filters";
import {
  findBinningStrategy,
  findDimensionById,
  findFilterDimensionById,
  findTemporalBucket,
} from "../utils/dimension-lookup";
import type {
  AvailableDimensionsResult,
  SourceDisplayInfo,
} from "../utils/dimension-picker";
import { getAvailableDimensionsForPicker } from "../utils/dimension-picker";
import {
  buildRawSeriesFromDefinitions,
  computeSourceBreakoutColors,
  getSelectedMetricsInfo,
} from "../utils/series";
import {
  createMeasureSourceId,
  createMetricSourceId,
  createSourceId,
} from "../utils/source-ids";
import {
  type TabInfo,
  createTabFromTabInfo,
  getDimensionsByType,
  resolveCommonTabLabel,
} from "../utils/tabs";

import { useDefinitionQueries } from "./use-definition-queries";
import { useViewerState } from "./use-viewer-state";
import { type LoadSourcesRequest, useViewerUrl } from "./use-viewer-url";

export interface UseMetricsViewerResult {
  definitions: MetricsViewerDefinitionEntry[];
  tabs: MetricsViewerTabState[];
  activeTab: MetricsViewerTabState | null;
  activeTabId: string | null;

  loadingIds: Set<MetricSourceId>;
  resultsByDefinitionId: Map<MetricSourceId, Dataset>;
  errorsByDefinitionId: Map<MetricSourceId, string>;
  modifiedDefinitions: Map<MetricSourceId, MetricDefinition>;
  isExecuting: (id: MetricSourceId) => boolean;

  series: SingleSeries[];
  cardIdToDefinitionId: Record<CardId, MetricSourceId>;
  activeBreakoutColors: SourceBreakoutColorMap;
  sourceColors: SourceColorMap;
  breakoutValuesBySourceId: Map<MetricSourceId, MetricBreakoutValuesResponse>;
  selectedMetrics: SelectedMetric[];
  sourceOrder: MetricSourceId[];
  sourceDataById: Record<MetricSourceId, SourceDisplayInfo>;
  availableDimensions: AvailableDimensionsResult;

  addMetric: (metric: SelectedMetric) => void;
  swapMetric: (oldMetric: SelectedMetric, newMetric: SelectedMetric) => void;
  removeMetric: (id: number, sourceType: "metric" | "measure") => void;
  changeTab: (tabId: string) => void;
  addAndSelectTab: (tabInfo: TabInfo) => void;
  removeTab: (tabId: string) => void;
  updateTab: (tabId: string, updates: Partial<MetricsViewerTabState>) => void;
  updateActiveTab: (updates: Partial<MetricsViewerTabState>) => void;
  changeTabDimension: (
    tabId: string,
    definitionId: MetricSourceId,
    dimension: DimensionMetadata,
  ) => void;
  removeTabDimension: (tabId: string, definitionId: MetricSourceId) => void;
  updateDefinition: (id: MetricSourceId, definition: MetricDefinition) => void;
  setBreakoutDimension: (
    id: MetricSourceId,
    dimension: ProjectionClause | undefined,
  ) => void;
}

function buildUrlRestoreTransform(
  sourceId: MetricSourceId,
  request: LoadSourcesRequest,
): ((definition: MetricDefinition) => MetricDefinition) | undefined {
  const breakoutInfo = request.breakoutBySourceId?.[sourceId];
  const filters = request.filtersBySourceId?.[sourceId];

  if (!breakoutInfo && (!filters || filters.length === 0)) {
    return undefined;
  }

  return (definition: MetricDefinition): MetricDefinition => {
    let result = definition;

    if (breakoutInfo) {
      const dimension = findDimensionById(result, breakoutInfo.dimensionId);
      if (dimension) {
        const dimensionRef = LibMetric.dimensionReference(dimension);

        let modifiedRef: LibMetric.ProjectionClause | null = null;
        if (breakoutInfo.temporalUnit) {
          const bucket = findTemporalBucket(
            result,
            dimension,
            breakoutInfo.temporalUnit,
          );
          if (bucket) {
            modifiedRef = LibMetric.withTemporalBucket(dimensionRef, bucket);
          }
        } else if (breakoutInfo.binning) {
          const strategy = findBinningStrategy(
            result,
            dimension,
            breakoutInfo.binning,
          );
          if (strategy) {
            modifiedRef = LibMetric.withBinning(dimensionRef, strategy);
          }
        }

        result = modifiedRef
          ? applyProjection(result, modifiedRef)
          : buildBinnedBreakoutDefinition(result, dimensionRef);
      }
    }

    if (filters) {
      for (const filter of filters) {
        const dimension = findFilterDimensionById(result, filter.dimensionId);
        if (dimension) {
          const clause = buildDimensionFilterClause(dimension, filter.value);
          result = LibMetric.filter(result, clause);
        }
      }
    }

    return result;
  };
}

export function useMetricsViewer({
  location,
}: MetricsViewerPageProps): UseMetricsViewerResult {
  const {
    state,
    loadingIds,
    removeDefinition,
    updateDefinition,
    selectTab: changeTab,
    addTab,
    removeTab,
    updateTab,
    setDefinitionDimension: changeTabDimension,
    removeDefinitionDimension: removeTabDimension,
    setBreakoutDimension,
    initialize,
    loadAndAddMetric,
    loadAndAddMeasure,
    loadAndReplaceMetric,
    loadAndReplaceMeasure,
  } = useViewerState();

  const handleLoadSources = useCallback(
    (request: LoadSourcesRequest) => {
      for (const metricId of request.metricIds) {
        const sourceId = createMetricSourceId(metricId);
        loadAndAddMetric(metricId, buildUrlRestoreTransform(sourceId, request));
      }
      for (const measureId of request.measureIds) {
        const sourceId = createMeasureSourceId(measureId);
        loadAndAddMeasure(
          measureId,
          buildUrlRestoreTransform(sourceId, request),
        );
      }
    },
    [loadAndAddMetric, loadAndAddMeasure],
  );

  useViewerUrl(state, initialize, handleLoadSources, location);

  const activeTab = useMemo((): MetricsViewerTabState | null => {
    if (state.tabs.length === 0) {
      return null;
    }
    return (
      state.tabs.find((tab) => tab.id === state.selectedTabId) ?? state.tabs[0]
    );
  }, [state.tabs, state.selectedTabId]);

  const {
    resultsByDefinitionId,
    errorsByDefinitionId,
    modifiedDefinitions,
    breakoutValuesBySourceId,
    isExecuting,
  } = useDefinitionQueries(state.definitions, activeTab);

  const selectedMetrics = useMemo(
    () => getSelectedMetricsInfo(state.definitions, loadingIds),
    [state.definitions, loadingIds],
  );

  const sourceBreakoutColors = useMemo(
    () =>
      computeSourceBreakoutColors(state.definitions, breakoutValuesBySourceId),
    [state.definitions, breakoutValuesBySourceId],
  );

  const { series, cardIdToDefinitionId, activeBreakoutColors } = useMemo(() => {
    if (!activeTab) {
      return { series: [], cardIdToDefinitionId: {}, activeBreakoutColors: {} };
    }
    return buildRawSeriesFromDefinitions(
      state.definitions,
      activeTab.dimensionMapping,
      activeTab.display,
      resultsByDefinitionId,
      modifiedDefinitions,
      sourceBreakoutColors,
      activeTab.visualizationSettings,
    );
  }, [
    state.definitions,
    activeTab,
    resultsByDefinitionId,
    modifiedDefinitions,
    sourceBreakoutColors,
  ]);

  const sourceColors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(activeBreakoutColors).map(([sourceId, colors]) => [
          sourceId,
          colors === undefined
            ? []
            : typeof colors === "string"
              ? [colors]
              : Array.from(colors.values()),
        ]),
      ),
    [activeBreakoutColors],
  );

  const definitionsBySourceId = useMemo(
    () =>
      objectFromEntries(
        state.definitions.map((entry) => [entry.id, entry.definition] as const),
      ),
    [state.definitions],
  );

  const sourceOrder = useMemo(
    () => state.definitions.map((entry) => entry.id),
    [state.definitions],
  );

  const sourceDataById = useMemo((): Record<
    MetricSourceId,
    SourceDisplayInfo
  > => {
    const result: Record<MetricSourceId, SourceDisplayInfo> = {};
    for (const entry of state.definitions) {
      const { definition } = entry;
      if (!definition) {
        continue;
      }
      const name = getDefinitionName(definition);
      if (!name) {
        continue;
      }
      if (LibMetric.sourceMetricId(definition) != null) {
        result[entry.id] = { type: "metric", name };
      } else if (LibMetric.sourceMeasureId(definition) != null) {
        result[entry.id] = { type: "measure", name };
      }
    }
    return result;
  }, [state.definitions]);

  const existingTabDimensionIds = useMemo(
    () =>
      new Set(
        state.tabs
          .flatMap((tab) => Object.values(tab.dimensionMapping))
          .filter(isNotNull),
      ),
    [state.tabs],
  );

  const effectiveTabs = useMemo(() => {
    const dimsBySource = new Map(
      state.definitions
        .filter((entry) => entry.definition != null)
        .map(
          (entry) =>
            [entry.id, getDimensionsByType(entry.definition!)] as const,
        ),
    );

    return state.tabs.map((tab) => {
      const names: string[] = [];
      for (const [sourceId, dimensionId] of getObjectEntries(
        tab.dimensionMapping,
      )) {
        if (dimensionId == null) {
          continue;
        }
        const sourceDimensions = dimsBySource.get(sourceId);
        const dimensionInfo = sourceDimensions?.get(dimensionId);
        if (dimensionInfo) {
          names.push(dimensionInfo.displayName);
        }
      }
      const label = resolveCommonTabLabel(names);
      return label != null && label !== tab.label ? { ...tab, label } : tab;
    });
  }, [state.tabs, state.definitions]);

  const availableDimensions = useMemo(
    () =>
      getAvailableDimensionsForPicker(
        definitionsBySourceId,
        sourceOrder,
        existingTabDimensionIds,
      ),
    [definitionsBySourceId, sourceOrder, existingTabDimensionIds],
  );

  const addMetric = useCallback(
    (metric: SelectedMetric) => {
      const sourceId = createSourceId(metric.id, metric.sourceType);

      if (state.definitions.some((entry) => entry.id === sourceId)) {
        return;
      }

      if (metric.sourceType === "metric") {
        loadAndAddMetric(metric.id);
      } else {
        loadAndAddMeasure(metric.id);
      }
    },
    [state.definitions, loadAndAddMetric, loadAndAddMeasure],
  );

  const swapMetric = useCallback(
    (oldMetric: SelectedMetric, newMetric: SelectedMetric) => {
      const oldSourceId = createSourceId(oldMetric.id, oldMetric.sourceType);

      if (newMetric.sourceType === "metric") {
        loadAndReplaceMetric(oldSourceId, newMetric.id);
      } else {
        loadAndReplaceMeasure(oldSourceId, newMetric.id);
      }
    },
    [loadAndReplaceMetric, loadAndReplaceMeasure],
  );

  const removeMetric = useCallback(
    (id: number, sourceType: "metric" | "measure") => {
      removeDefinition(createSourceId(id, sourceType));
    },
    [removeDefinition],
  );

  const addAndSelectTab = useCallback(
    (tabInfo: TabInfo) => {
      const newTab = createTabFromTabInfo(tabInfo);
      if (!newTab) {
        return;
      }

      addTab(newTab);
      changeTab(newTab.id);
    },
    [addTab, changeTab],
  );

  const updateActiveTab = useCallback(
    (updates: Partial<MetricsViewerTabState>) => {
      if (!activeTab) {
        return;
      }
      updateTab(activeTab.id, updates);
    },
    [activeTab, updateTab],
  );

  return {
    definitions: state.definitions,
    tabs: effectiveTabs,
    activeTab,
    activeTabId: state.selectedTabId,

    loadingIds,
    resultsByDefinitionId,
    errorsByDefinitionId,
    modifiedDefinitions,
    isExecuting,

    series,
    cardIdToDefinitionId,
    activeBreakoutColors,
    sourceColors,
    breakoutValuesBySourceId,
    selectedMetrics,
    sourceOrder,
    sourceDataById,
    availableDimensions,

    addMetric,
    swapMetric,
    removeMetric,
    changeTab,
    addAndSelectTab,
    removeTab,
    updateTab,
    updateActiveTab,
    changeTabDimension,
    removeTabDimension,
    updateDefinition,
    setBreakoutDimension,
  };
}
